// frontend/src/utils/etlMappingUtils.js
import { TRANSFORM_RULES } from './transformRules';

/**
 * Catálogo de reglas agrupado por categoría — DERIVADO de TRANSFORM_RULES.
 * RuleBuilderModal.jsx consume este objeto para renderizar las categorías.
 * El label es fallback ES; la UI usa t('wizard.mapping.rule_names.${id}') primero.
 *
 * ⚠️  Para agregar una regla nueva: hacerlo SOLO en transformRules.js.
 *     Este catálogo se regenera automáticamente.
 */
export const ruleCatalog = TRANSFORM_RULES.reduce((acc, rule) => {
  const cat = rule.category;
  if (!acc[cat]) acc[cat] = [];
  acc[cat].push({
    id: rule.id,
    label: rule.label,
    needsParams: rule.params.length > 0,
  });
  return acc;
}, {});

/** Evalúa si un valor está vacío/nulo/undefined */
export const isMissing = (val) => val === undefined || val === null || val === '';

/**
 * Cuenta cuántas reglas del pipeline tienen parámetros obligatorios sin llenar.
 * Usado por MappingTable para mostrar badge de "N reglas incompletas".
 */
export const getIncompleteRulesCount = (pipeline) => {
  if (!pipeline || pipeline.length === 0) return 0;
  return pipeline.filter(rule => {
    if (rule.id === 'REPLACE') return isMissing(rule.params.search);
    if (rule.id === 'TRUNCATE') return isMissing(rule.params.max_length);
    if (rule.id === 'PAD_LEFT' || rule.id === 'PAD_RIGHT') return isMissing(rule.params.total_length);
    if (rule.id === 'ADD_PREFIX' || rule.id === 'ADD_SUFFIX') return isMissing(rule.params.text);
    if (rule.id === 'SUBSTRING') return isMissing(rule.params.start) || isMissing(rule.params.end);
    if (rule.id === 'EXTRACT_END') return isMissing(rule.params.length);
    if (rule.id === 'SPLIT_GET') return isMissing(rule.params.delimiter) || isMissing(rule.params.part);
    if (rule.id === 'SPLIT_FIELD') return isMissing(rule.params.separator);
    if (rule.id === 'DEFAULT_NULL') return isMissing(rule.params.value);
    if (rule.id === 'FORMAT_CODE') return isMissing(rule.params.prefix) || isMissing(rule.params.totalLength);
    if (rule.id === 'STRIP_CODE') return isMissing(rule.params.prefix);
    if (rule.id === 'SET_CURRENT_DATE') return isMissing(rule.params.dateFormat);
    if (rule.id === 'MATH_ROUND') return isMissing(rule.params.decimals);
    if (rule.id === 'MATH_ADD_SUB' || rule.id === 'MATH_MULTIPLY' || rule.id === 'MATH_DIVIDE') return isMissing(rule.params.value);
    if (rule.id === 'DATE_EXTRACT') return isMissing(rule.params.part);
    if (rule.id === 'SET_FIXED') return isMissing(rule.params.value);

    if (rule.id === 'TRANSLATE_VALUE') {
      const m = rule.params.mappings;
      return !m || (typeof m === 'string' && m.trim() === '') || (typeof m === 'object' && Object.keys(m).length === 0);
    }

    if (rule.id === 'ADVANCED_COND') {
      if (isMissing(rule.params.operator) || isMissing(rule.params.result)) return true;
      if (!['IS_EMPTY', 'NOT_EMPTY'].includes(rule.params.operator) && isMissing(rule.params.condition_val)) return true;
      return false;
    }

    if (rule.id === 'LOOKUP') {
      if (isMissing(rule.params.lookupTable) || isMissing(rule.params.lookupField) || isMissing(rule.params.returnField)) return true;
      if (rule.params.filterField && isMissing(rule.params.filterValue)) return true;
      if (rule.params.onNotFound === 'default' && isMissing(rule.params.defaultValue)) return true;
      return false;
    }

    if (rule.id === 'FORMAT_DATE') {
      if (isMissing(rule.params.from) || isMissing(rule.params.to)) return true;
      if (rule.params.from === 'CUSTOM' && isMissing(rule.params.custom_from)) return true;
      if (rule.params.to === 'CUSTOM' && isMissing(rule.params.custom_to)) return true;
      return false;
    }

    if (rule.id === 'JSON_EXTRACT') return isMissing(rule.params.path);

    return false;
  }).length;
};
